import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { AdminDashboard, FinanceiroDashboard, SuporteDashboard } from '../pages/dashboards';
import api from '../services/useApi';
import { dashboardService } from '../services/dashboardService';
import Swal from 'sweetalert2';


const DashboardSelector: React.FC = () => {
  const { user } = useAuth();

  // Verifica se há notas NFCom pendentes de transmissão ao carregar o dashboard
  useEffect(() => {
    const verificarNotasRejeitadas = async () => {
      try {
        const response = await api.get('/notas-nfcom/status/rejeitado');
        const notasRejeitadas = response.data?.data ?? response.data;

        if (Array.isArray(notasRejeitadas) && notasRejeitadas.length > 0) {
          await Swal.fire({
            icon: 'error',
            title: 'Notas NFCom Rejeitadas!',
            html: `
              <div style="text-align: center;">
                <p style="font-size: 16px; margin-bottom: 10px;">
                  Existem <strong>${notasRejeitadas.length}</strong> ${notasRejeitadas.length === 1 ? 'nota NFCom rejeitada' : 'notas NFCom rejeitadas'}.
                </p>
                <p style="font-size: 14px; color: #666;">
                  Acesse a página de Notas NFCom para corrigir e retransmiti-las.
                </p>
              </div>
            `,
            confirmButtonText: 'Entendi',
            confirmButtonColor: '#3085d6',
            showCancelButton: true,
            cancelButtonText: 'Ir para Notas NFCom',
            cancelButtonColor: '#d33',
            customClass: {
              popup: 'swal-zindex',
              container: 'swal-zindex'
            }
          }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
              window.location.href = '/notas-nfcom';
            }
          });
        }
      } catch (error) {
        console.error('Erro ao verificar notas rejeitadas:', error);
      }
    };

    const verificarNotasPendentes = async () => {
      try {
        const response = await api.get('/notas-nfcom/status/pendente_transmissao');
        const notasPendentes = response.data?.data ?? response.data;
        
        if (Array.isArray(notasPendentes) && notasPendentes.length > 0) {
          await Swal.fire({
            icon: 'warning',
            title: 'Notas NFCom Pendentes!',
            html: `
              <div style="text-align: center;">
                <p style="font-size: 16px; margin-bottom: 10px;">
                  Existem <strong>${notasPendentes.length}</strong> ${notasPendentes.length === 1 ? 'nota NFCom pendente' : 'notas NFCom pendentes'} de transmissão.
                </p>
                <p style="font-size: 14px; color: #666;">
                  Acesse a página de Notas NFCom para transmiti-las.
                </p>
              </div>
            `,
            confirmButtonText: 'Entendi',
            confirmButtonColor: '#3085d6',
            showCancelButton: true,
            cancelButtonText: 'Ir para Notas NFCom',
            cancelButtonColor: '#d33',
            customClass: {
              popup: 'swal-zindex',
              container: 'swal-zindex'
            }
          }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
              // Redireciona para a página de notas NFCom
              window.location.href = '/notas-nfcom';
            }
          });
        }
      } catch (error) {
        console.error('Erro ao verificar notas pendentes:', error);
      }
    };

    const verificarTarefasPendentes = async () => {
      try {
        const response = await api.get('/tarefas/dashboard');
        // A resposta do useApi retorna o objeto AxiosResponse, e o backend retorna { success, data }
        const stats = response.data?.data;
        
        if (stats && stats.totalAlertas > 0) {
          await Swal.fire({
            icon: 'info',
            title: 'Tarefas do Dia!',
            html: `
              <div style="text-align: center;">
                <p style="font-size: 16px; margin-bottom: 10px;">
                  Você tem <strong>${stats.totalAlertas}</strong> tarefa(s) pendente(s) ou atrasada(s).
                </p>
                <p style="font-size: 14px; color: #666;">
                  ${stats.atrasadas > 0 ? `<strong>${stats.atrasadas}</strong> em atraso e ` : ''}
                  <strong>${stats.pendentesHoje}</strong> para hoje.
                </p>
              </div>
            `,
            confirmButtonText: 'Entendi',
            confirmButtonColor: '#3085d6',
            showCancelButton: true,
            cancelButtonText: 'Ir para Tarefas',
            cancelButtonColor: '#ed6c02',
            customClass: {
              popup: 'swal-zindex',
              container: 'swal-zindex'
            }
          }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
              window.location.href = '/tarefas';
            }
          });
        }
      } catch (error) {
        console.error('Erro ao verificar tarefas pendentes:', error);
      }
    };

    const verificarAlertasTrocaOleo = async () => {
      try {
        const alertas = await dashboardService.getAlertasTrocaOleo(100);
        if (!alertas.length) return;

        const temVencido = alertas.some((a) => a.urgencia === 'vencido');
        const itensHtml = alertas.map((alerta) => {
          const status = alerta.urgencia === 'vencido'
            ? `Troca <strong>vencida</strong> há ${Math.abs(alerta.km_restantes).toLocaleString('pt-BR')} km`
            : `Faltam <strong>${alerta.km_restantes.toLocaleString('pt-BR')} km</strong> para a próxima troca`;

          return `
            <li style="text-align: left; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #eee;">
              <strong>${alerta.placa}</strong> — ${alerta.marca} ${alerta.modelo}<br/>
              <span style="font-size: 13px; color: #666;">${status}</span><br/>
              <span style="font-size: 12px; color: #888;">
                KM atual (último abastecimento): ${alerta.km_atual.toLocaleString('pt-BR')} km ·
                Próxima troca: ${alerta.proxima_troca_km.toLocaleString('pt-BR')} km
              </span>
            </li>
          `;
        }).join('');

        await Swal.fire({
          icon: temVencido ? 'error' : 'warning',
          title: 'Troca de óleo próxima!',
          html: `
            <div style="text-align: center;">
              <p style="font-size: 16px; margin-bottom: 12px;">
                ${alertas.length === 1 ? '1 veículo precisa' : `${alertas.length} veículos precisam`} de atenção na troca de óleo (≤ 100 km).
              </p>
              <ul style="list-style: none; padding: 0; margin: 0; max-height: 280px; overflow-y: auto;">
                ${itensHtml}
              </ul>
            </div>
          `,
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#3085d6',
          showCancelButton: true,
          cancelButtonText: 'Ir para Trocas de Óleo',
          cancelButtonColor: '#ed6c02',
          width: 520,
          customClass: {
            popup: 'swal-zindex',
            container: 'swal-zindex',
          },
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel) {
            window.location.href = '/frota/trocas-oleo';
          }
        });
      } catch (error) {
        console.error('Erro ao verificar alertas de troca de óleo:', error);
      }
    };

    verificarNotasRejeitadas();
    verificarNotasPendentes();
    verificarTarefasPendentes();
    verificarAlertasTrocaOleo();
  }, []);

  // Se não há usuário, não renderiza nada (deve ser tratado pelo ProtectedRoute)
  if (!user) {
    return null;
  }

  // Seleciona o dashboard baseado no perfil do usuário
  const renderDashboard = () => {
    switch (user.perfil) {
      case 'administrador':
        return <AdminDashboard />;
      
      case 'financeiro':
        return <FinanceiroDashboard />;
      
      case 'suporte':
        return <SuporteDashboard />;
      
      case 'bot':
        // Para bots, usar o dashboard de administrador como fallback
        return <AdminDashboard />;
      
      default:
        // Fallback para perfis não reconhecidos
        console.warn(`Perfil não reconhecido: ${user.perfil}. Usando dashboard de administrador como fallback.`);
        return <AdminDashboard />;
    }
  };

  return (
    <Box>
      {renderDashboard()}
    </Box>
  );
};

export default DashboardSelector; 